import type { ReactNode } from 'react';
import {
  Boxes,
  Box,
  Code2,
  Database,
  Globe,
  Layers,
  User,
  Users,
  Webhook,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Badge,
  Button,
  EntityRef,
  Icon,
  PageHeader,
  Text,
} from '../../components/ui';
import { entityRefRoute, lifecycleColor } from './entityMeta';
import { useT } from '../i18n';

/** Featured-icon glyph per entity kind. Falls back to a generic box. */
const kindIcon: Record<string, LucideIcon> = {
  component: Box,
  api: Webhook,
  user: User,
  group: Users,
  system: Boxes,
  domain: Globe,
  resource: Database,
  location: Layers,
};

/**
 * The eyebrow above the title: `KIND — TYPE` uppercased (e.g.
 * `COMPONENT — SERVICE`, `API — OPENAPI`), or just `KIND` when the entity has
 * no `spec.type` (User, Group). Empty when the kind is unknown.
 */
export function entityEyebrow(kind?: string, type?: string): string {
  if (!kind) return '';
  const base = kind.toLocaleUpperCase('en-US');
  return type ? `${base} — ${type.toLocaleUpperCase('en-US')}` : base;
}

/** A label/value column in the header meta row. */
const MetaField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <Text variant="caption" tone="tertiary">
      {label}
    </Text>
    {children}
  </div>
);

export interface EntityPageHeaderViewProps {
  /** Entity display name (title ?? name). */
  title: string;
  /** Entity kind, e.g. "Component", "User", "API". */
  kind?: string;
  /** spec.type, e.g. "service", "openapi". */
  type?: string;
  description?: string;
  /** Owned-by refs; the primary owner first. Each links to its entity page. */
  ownerRefs?: string[];
  lifecycle?: string;
  tags?: string[];
  /** Browsable source URL (from the source-location annotation). */
  sourceUrl?: string;
  /** The favorite star, wired by the Backstage wrapper. */
  favorite?: ReactNode;
}

/**
 * Regask-styled entity page header — replaces Backstage's stock `EntityHeader`
 * bar so entity pages open with the same design-system header as the rest of
 * the app. Composes the `PageHeader` primitive: kind/type eyebrow, kind icon,
 * title with a favorite star, a "View Source" action, and an Owner / Lifecycle
 * / Tags meta row. It carries everything the old About card showed, so the
 * Overview tab can lead straight with the content cards.
 * Presentational and Backstage-free so Storybook can render it directly.
 */
export const EntityPageHeaderView = ({
  title,
  kind,
  type,
  description,
  ownerRefs = [],
  lifecycle,
  tags = [],
  sourceUrl,
  favorite,
}: EntityPageHeaderViewProps) => {
  const t = useT();
  const KindIcon = kindIcon[kind?.toLocaleLowerCase('en-US') ?? ''] ?? Box;
  const hasMeta = ownerRefs.length > 0 || Boolean(lifecycle) || tags.length > 0;
  return (
    // `gridArea: pageHeader` makes the header span all three columns of the
    // catalog entity `<Page>` grid (the stock Header set the same area). Without
    // it the element auto-places into a narrow `auto`-width cell and the title
    // bar doesn't stretch full width. Inert outside that grid (e.g. in
    // Storybook), so the view stays portable.
    <div className="px-6 pt-6" style={{ gridArea: 'pageHeader' }}>
      <PageHeader
        className="!mb-6"
        eyebrow={entityEyebrow(kind, type)}
        icon={<Icon as={KindIcon} size="xl" />}
        title={title}
        titleAdornment={favorite}
        description={description}
        actions={
          sourceUrl ? (
            <Button
              variant="secondary"
              size="sm"
              iconLeading={<Icon as={Code2} size="sm" />}
              onPress={() => window.open(sourceUrl, '_blank', 'noopener')}
            >
              {t('catalog.entityPageHeaderView.viewSource')}
            </Button>
          ) : undefined
        }
        meta={
          hasMeta ? (
            <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
              {ownerRefs.length > 0 && (
                <MetaField
                  label={t(
                    ownerRefs.length > 1
                      ? 'catalog.entityPageHeaderView.owners'
                      : 'catalog.entityPageHeaderView.owner',
                  )}
                >
                  <span className="flex flex-wrap gap-x-3 gap-y-1.5">
                    {ownerRefs.map(ref => (
                      <EntityRef
                        key={ref}
                        entityRef={ref}
                        href={entityRefRoute(ref)}
                      />
                    ))}
                  </span>
                </MetaField>
              )}
              {lifecycle && (
                <MetaField label={t('catalog.entityPageHeaderView.lifecycle')}>
                  <span>
                    <Badge color={lifecycleColor(lifecycle)} dot>
                      {lifecycle}
                    </Badge>
                  </span>
                </MetaField>
              )}
              {tags.length > 0 && (
                <MetaField label={t('catalog.entityPageHeaderView.tags')}>
                  <span className="flex flex-wrap gap-1.5">
                    {tags.map(tag => (
                      <Badge key={tag} color="gray">
                        {tag}
                      </Badge>
                    ))}
                  </span>
                </MetaField>
              )}
            </div>
          ) : undefined
        }
      />
    </div>
  );
};
